import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";
import LiveMeetingPanel from "./LiveMeetingPanel";

export default function GlobalMeetingReminder() {
  const { token, user } = useAuth();

  const [appointments, setAppointments] = useState([]);
  const [liveMeeting, setLiveMeeting] = useState(null);
  const [remindedId, setRemindedId] = useState(null);

  /* ======================
     REQUEST NOTIFICATION PERMISSION
  ====================== */
  useEffect(() => {
    if ("Notification" in window) {
      if (Notification.permission !== "granted") {
        Notification.requestPermission();
      }
    }
  }, []);

  /* ======================
     FETCH ACCEPTED APPOINTMENTS
  ====================== */
  const fetchAppointments = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/appointments/calendar`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await res.json();

      const accepted = (data.appointments || []).filter(
        (a) => a.status === "ACCEPTED"
      );

      setAppointments(accepted);
    } catch (err) {
      console.error("Reminder fetch error:", err);
    }
  };

  /* ======================
     POLLING
  ====================== */
  useEffect(() => {
    if (!token) return;

    fetchAppointments();

    const interval = setInterval(fetchAppointments, 2500);
    return () => clearInterval(interval);
  }, [token]);

  /* ======================
     CHECK LIVE MEETING
  ====================== */
  useEffect(() => {
    checkLiveAppointments();
  }, [appointments]);

  const checkLiveAppointments = () => {
    const now = new Date();

    const running = appointments.find((app) => {
      if (app.appointmentCallStatus === "COMPLETED") return false;

      const start = new Date(app.appointmentDate);
      const end = new Date(
        start.getTime() + (app.appointmentDurationMinutes || 30) * 60000
      );

      const meetingTimeStarted = now >= start && now <= end;
      const solicitorJoinedEarly =
        app.solicitorCallJoinStatus === "JOINED";

      if (user?.role === "USER") {
        if (app.userCallJoinStatus === "JOINED") return false;
        if (app.userCallJoinStatus === "ENDED") return false;

        return meetingTimeStarted || solicitorJoinedEarly;
      }

      if (user?.role === "SOLICITOR") {
        if (app.solicitorCallJoinStatus === "JOINED") return false;
        if (app.solicitorCallJoinStatus === "ENDED") return false;

        return meetingTimeStarted;
      }

      return false;
    });

    if (running) {
      setLiveMeeting(running);

      if (remindedId !== running._id) {

        // ✅ Toast inside browser
        toast.success("🔴 Your meeting is live. Join now!", {
          position: "top-right",
          autoClose: 6000
        });

        // ✅ SYSTEM NOTIFICATION (Outside Browser)
        if ("Notification" in window && Notification.permission === "granted") {
          const notification = new Notification("🔴 Live Legal Consultation", {
            body: "Your meeting is live. Click to join now.",
            icon: "/logo192.png"
          });

          notification.onclick = () => {
            window.focus();
            window.open(running.videoCallLink, "_blank");
          };
        }

        setRemindedId(running._id);
      }

    } else {
      setLiveMeeting(null);
    }
  };

  /* ======================
     UPDATE CALL STATUS
  ====================== */
  const updateCallStatus = async (id, status) => {
    try {
      await fetch(
        `${API_BASE_URL}/api/appointments/call-status/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ status })
        }
      );
    } catch (err) {
      console.error("Call status update failed:", err);
    }
  };

  return (
    <>
      <LiveMeetingPanel
        meeting={
          liveMeeting
            ? {
              start: liveMeeting.appointmentDate,
              extendedProps: {
                videoCallLink: liveMeeting.videoCallLink,
                solicitorName: liveMeeting.solicitorId?.name,
                clientName: liveMeeting.userDetails?.name,
                solicitorPhoto: liveMeeting.solicitorId?.profile?.photo,
                clientPhoto: liveMeeting.userId?.userProfile?.photo,
                meetingTitle: "Legal Consultation"
              }
            }
            : null
        }
        onJoinMeeting={async () => {
          const id = liveMeeting?._id;
          await updateCallStatus(id, "JOINED");
          setLiveMeeting(null);
        }}
        onEndMeeting={async () => {
          const id = liveMeeting?._id;
          await updateCallStatus(id, "ENDED");
          setLiveMeeting(null);
        }}
      />
    </>
  );
}
